
import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { get_category } from './store/reducers/homeReducer.js';
import { get_current_customer } from './store/reducers/authReducer.js';
import { useSocketBanCheck } from './hooks/useSocketBanCheck'

// ── Eager: landing page + always-mounted global chrome ───────────────
// Home is the critical first paint; the global widgets are tiny and must
// be present immediately (no Suspense flicker on the shell).
import Home from './pages/Home';
import ErrorBoundary from './components/error/ErrorBoundary'
import NotFoundPage from './components/error/NotFoundPage'
import Dashboard from './pages/Dashboard';
import ProtectUser from './utils/ProtectUser';
import NotificationBanner from './components/NotificationBanner.jsx'
import MobileBottomNav from './components/MobileBottomNav'
import NotificationToast from './components/NotificationToast'
import NotificationModal from './components/NotificationModal'
import ScrollToTop from './components/ScrollToTop.jsx'
import { buildDashboardUrl } from './utilies/panelUrls.js'
import { NoIndexPage } from './components/seo/NoIndexHelmet.jsx'
import FloatingQuickMenu from './components/FloatingQuickMenu.jsx'
import MobileFloatingQuickMenu from './components/MobileFloatingQuickMenu.jsx'
import WeeklyDealsGateway from './components/home/WeeklyDealsGateway.jsx'
import SupportRealtimeListener from './components/SupportRealtimeListener.jsx'
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt.jsx'
import MobileAppDownload from './pages/MobileAppDownload.jsx'
import GameRadioPlayer from './components/GameRadioPlayer.jsx'
// ── Lazy: every non-critical route is split into its own chunk ───────
// Same default exports as before — only the import strategy changed.
const Card = lazy(() => import('./pages/Card'))
const Details = lazy(() => import('./pages/Details'))
const Register = lazy(() => import('./pages/Register'))
const Login = lazy(() => import('./pages/Login.jsx'))
const Shipping = lazy(() => import('./pages/Shipping'))
const CategoryShops = lazy(() => import('./pages/CategoryShop'))
const SearchProducts = lazy(() => import('./pages/SearchProducts'))
const SearchShowcase = lazy(() => import('./pages/SearchShowcase'))
const Payment = lazy(() => import('./pages/Payment'))
const DigitalProductListing = lazy(() => import('./pages/DigitalProductListing'))
const Index = lazy(() => import('./components/dashboard/Index.jsx'))
const Orders = lazy(() => import('./components/dashboard/Orders.jsx'))
const Wishlist = lazy(() => import('./components/dashboard/Wishlist'))
const ChangePassword = lazy(() => import('./components/dashboard/ChangePassword.jsx'))
const Order = lazy(() => import('./components/dashboard/Order'))
const Chat = lazy(() => import('./components/dashboard/Chat'))
const About = lazy(() => import('./components/dashboard/About.jsx'))
const Contact = lazy(() => import('./components/dashboard/Contact.jsx'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ShopPage = lazy(() => import('./pages/ShopPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage.jsx'))
const DigitalProducts = lazy(() => import('./pages/DigitalProducts'))
const DigitalProductDetails = lazy(() => import('./pages/DigitalProductDetails'))
const OfficialStore = lazy(() => import('./pages/OfficialStore'))
const OfficialCategory = lazy(() => import('./pages/OfficialCategory'))
const OfficialService = lazy(() => import('./pages/OfficialService'))
const MyBalance = lazy(() => import('./components/dashboard/MyBalance.jsx'))
const BadgesPage = lazy(() => import('./pages/BadgesPage.jsx'))
const Blogs = lazy(() => import('./pages/Blogs.jsx'))
const BlogDetail = lazy(() => import('./pages/BlogDetail.jsx'))
const Market = lazy(() => import('./pages/Market.jsx'))
const WeeklyDeals = lazy(() => import('./pages/WeeklyDeals.jsx'))
// Phase 4H: VitrinProducts səhifəsi artıq marşrutlanmır — /bazar?type=vitrin istifadə olunur
const Stores = lazy(() => import('./pages/Stores.jsx'))
const Fag = lazy(() => import('./pages/Fag.jsx'))
const MyFollowing = lazy(() => import('./pages/MyFollowing.jsx'))
const MyCashback = lazy(() => import('./components/dashboard/MyCashback.jsx'))
const MyGiveaways = lazy(() => import('./components/dashboard/MyGiveaways.jsx'))
const SiteReviews = lazy(() => import('./pages/SiteReviews.jsx'))
const BecomeSeller = lazy(() => import('./pages/BecomeSeller.jsx'))
const Support = lazy(() => import('./pages/Support.jsx'))
const SupportTickets = lazy(() => import('./pages/SupportTickets.jsx'))
const SupportCreate = lazy(() => import('./pages/SupportCreate.jsx'))
const SupportTicketDetail = lazy(() => import('./pages/SupportTicketDetail.jsx'))
const Giveaways = lazy(() => import('./pages/Giveaways.jsx'))
const GiveawayDetail = lazy(() => import('./pages/GiveawayDetail.jsx'))
const Campaigns = lazy(() => import('./pages/Campaigns.jsx'))
const CampaignDetail = lazy(() => import('./pages/CampaignDetail.jsx'))
const OAuthSuccess = lazy(() => import('./pages/OAuthSuccess'))
const TicketDetail = lazy(() => import('./pages/TicketDetails.jsx'))
const SecurityCenter = lazy(() => import('./components/dashboard/SecurityCenter.jsx'))
const Profile = lazy(() => import('./components/dashboard/Profile.jsx'))
const SellerHub = lazy(() => import('./pages/SellerHub.jsx'))
const Categories = lazy(() => import('./pages/Categories.jsx'))
const CategoryDetail = lazy(() => import('./pages/CategoryDetail.jsx'))
const TelegramBot = lazy(() => import('./pages/TelegramBot.jsx'))

// Lightweight route-transition fallback — matches existing spinner look.
const RouteFallback = () => {
  const { t } = useTranslation()
  return (
  <div className='flex min-h-[60vh] w-full items-center justify-center'>
    <div className='flex flex-col items-center gap-3 text-slate-500'>
      <div className='h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent' />
      <span className='text-sm font-medium'>{t('loading')}</span>
    </div>
  </div>
  )
}

const DashboardAppRedirect = () => {
  useEffect(() => {
    window.location.replace(buildDashboardUrl(window.location.pathname + window.location.search))
  }, [])

  return null
}

function App() {
  useSocketBanCheck()
  const dispatch = useDispatch()
  
  useEffect(() => {
    dispatch(get_category())
    dispatch(get_current_customer())
  }, [dispatch])
  
  return (
    <div className='max-w-screen min-h-screen'>
      <BrowserRouter>
      <ScrollToTop />
      <MobileBottomNav />
      <FloatingQuickMenu />
      <MobileFloatingQuickMenu />
      <GameRadioPlayer />
      <WeeklyDealsGateway />
      <SupportRealtimeListener />
      <NotificationBanner />
      <NotificationToast />
      <NotificationModal />
      <NotificationPermissionPrompt />
        <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* ============================================ */}
          {/* AUTH ROUTES */}
          {/* ============================================ */}
          <Route path='/register' element={<NoIndexPage title='Qeydiyyat - ItemZo'><Register /></NoIndexPage>} />
          <Route path='/login' element={<NoIndexPage title='Giriş - ItemZo'><Login /></NoIndexPage>} />  
          <Route path='/forgot-password' element={<NoIndexPage title='Şifrə bərpası - ItemZo'><ForgotPassword /></NoIndexPage>} />
          <Route path='/oauth-success' element={<NoIndexPage title='Giriş - ItemZo'><OAuthSuccess /></NoIndexPage>} />
          <Route path='/seller/dashboard/*' element={<DashboardAppRedirect />} />
          <Route path='/admin/dashboard/*' element={<DashboardAppRedirect />} />

          
          {/* ============================================ */}
          {/* PUBLIC PAGES */}
          {/* ============================================ */}
          <Route path='/' element={<Home />} />
          <Route path='/kateqoriyalar' element={<Categories />} />
          <Route path='/kateqoriya/:slug' element={<CategoryDetail />} />
          {/* Route consistency fix: /shops used to mount the legacy product
              grid (Shops.jsx) — it now redirects to the real stores page.
              Marketplace lives at /bazar (+ /market alias). */}
          <Route path='/shops' element={<Navigate to='/magazalar' replace />} />
          <Route path='/card' element={<NoIndexPage title='Səbət - ItemZo'><Card /></NoIndexPage>} />
          <Route path='/About' element={<About />} />
          <Route path='/contact' element={<Contact />} />
          <Route path='/fag' element={<Fag />} />
          <Route path='/reviews' element={<SiteReviews />} />
          <Route path='/satici-ol' element={<BecomeSeller />} />
          <Route path='/seller-hub' element={<SellerHub />} />
          <Route path='/telegram-bildirim-botu' element={<TelegramBot />} />
          {/* /seller-demo kept as a backward-compatible alias → /seller-hub */}
          <Route path='/seller-demo' element={<Navigate to='/seller-hub' replace />} />
          <Route path='/support' element={<Support />} />
          <Route path='/support/tickets' element={<SupportTickets />} />
          <Route path='/support/tickets/create' element={<SupportCreate />} />
          <Route path='/support/tickets/:id' element={<SupportTicketDetail />} />
          <Route path="/mobile-app" element={<MobileAppDownload />} />    
          <Route path="/shop/:username" element={<ShopPage />} />
          {/* ============================================ */}
          {/* SELLER PRODUCTS (Normal Products) */}
          {/* ============================================ */}
          <Route path='/products?' element={<CategoryShops />} />
          <Route path='/products/search?' element={<SearchProducts />} />
          <Route path='/axtaris-vitrini' element={<SearchShowcase />} />
          <Route path='/product/details/:slug' element={<Details />} /> 
          <Route path='/blogs' element={<Blogs />} />
              <Route path='/blog/:slug' element={<BlogDetail />} />

          {/* ✅ İLAN PAZARI — Phase 4H: /bazar əsas marketplace URL-dir */}
          <Route path='/bazar' element={<Market />} />
          <Route path='/market' element={<Market />} />
          <Route path='/haftanin-firsatlari' element={<WeeklyDeals />} />
          {/* Phase 4H: ayrıca vitrin səhifəsi ləğv — /bazar?type=vitrin-ə yönləndir */}
          <Route path='/vitrin-mehsullari' element={<Navigate to='/bazar?type=bumped' replace />} />

          {/* ✅ MARKETING — Phase 3 */}
          <Route path='/cekilisler' element={<Giveaways />} />
          <Route path='/cekilis/:slug' element={<GiveawayDetail />} />
          <Route path='/kampaniyalar' element={<Campaigns />} />
          <Route path='/kampaniya/:slug' element={<CampaignDetail />} />

          {/* ✅ MAGAZALAR */}
          <Route path='/magazalar' element={<Stores />} />
          <Route path="/shop/:username/badges" element={<BadgesPage />} />
          <Route path="/rozetler" element={<BadgesPage />} />
          {/* <Route path='/ticket/:ticketId' element={<TicketDetail />} /> */}
          <Route path='/ticket/:ticketId' element={<NoIndexPage title='Dəstək müraciəti - ItemZo'><TicketDetail /></NoIndexPage>} />
          {/* ============================================ */}
          {/* ✅ YENİ - DIGITAL PRODUCTS (Admin Products) */}
          {/* ============================================ */}
          <Route path='/digital/:category' element={<DigitalProducts />} />
          <Route path='/digital/:category/:subCategory' element={<DigitalProducts />} />
          <Route path='/digital/product/:slug' element={<DigitalProductDetails />} />
          <Route path='/digital-products' element={<DigitalProductListing />} />
          {/* ✅ Rəsmi məhsul mağazası (oyun/brend səhifələri) */}
          <Route path='/official' element={<OfficialStore />} />
          <Route path='/official/:catSlug/:serviceSlug' element={<OfficialService />} />
          <Route path='/official/:slug' element={<OfficialCategory />} />
          
          {/* ============================================ */}
          {/* CHECKOUT */}
          {/* ============================================ */}
          <Route path='/shipping' element={<NoIndexPage title='Çatdırılma - ItemZo'><Shipping /></NoIndexPage>} />
          <Route path='/payment' element={<NoIndexPage title='Ödəniş - ItemZo'><Payment /></NoIndexPage>} />
          
          {/* ============================================ */}
          {/* USER DASHBOARD (Protected) */}
          {/* ============================================ */}
          <Route path='/dashboard' element={<NoIndexPage title='Hesab - ItemZo'><ProtectUser /></NoIndexPage>}>
            <Route path='' element={<Dashboard />}>
              <Route path='' element={<Index />} />
              <Route path='profile' element={<Profile />} />
              <Route path='my-orders' element={<Orders />} />
              <Route path='my-wishlist' element={<Wishlist />} />
              <Route path='order/details/:orderId' element={<Order />} />  
              <Route path='chage-password' element={<ChangePassword />} />
              <Route path='chat' element={<Chat />} />    
              <Route path='chat/direct/:conversationId' element={<Chat />} />
              <Route path='chat/:sellerId' element={<Chat />} />
              <Route path='/dashboard/my-balance' element={<MyBalance />} />
              <Route path='notifications' element={<NotificationsPage />} />
              <Route path='following' element={<MyFollowing />} />
              <Route path='cashback' element={<MyCashback />} />
              <Route path='giveaways' element={<MyGiveaways />} />
              <Route path='security' element={<SecurityCenter />} />
              <Route path='access-logs' element={<SecurityCenter logsOnly />} />
             

            </Route>
          </Route>

          {/* Naməlum route → 404 */}
          <Route path='*' element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}
   
export default App;
